import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/50 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <article className="prose prose-invert prose-lg max-w-none">
          <h1 className="text-4xl font-bold mb-8 text-white">Terms of Service</h1>
          
          <p className="text-gray-300 mb-6">
            <strong>Effective Date:</strong> January 1, 2025
          </p>

          <p className="text-gray-300 mb-6">
            Welcome to Gam Ticket. These Terms of Service ("Terms") govern your use of our mobile application and website (collectively, the "Service"). By accessing or using the Service, you agree to be bound by these Terms.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4 text-white">1. Acceptance of Terms</h2>
          <p className="text-gray-300 mb-6">
            By creating an account or using the Service, you confirm that you are at least 13 years old and agree to comply with these Terms. If you do not agree, please do not use the Service.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4 text-white">2. Description of Service</h2>
          <p className="text-gray-300 mb-6">
            Gam Ticket provides a platform for purchasing tickets to events, including but not limited to football matches, concerts, and festivals in The Gambia. We facilitate transactions between event organizers and ticket buyers.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4 text-white">3. User Accounts</h2>
          <ul className="list-disc pl-6 text-gray-300 mb-6 space-y-2">
            <li>You must provide accurate and complete information when creating an account.</li>
            <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
            <li>You are responsible for all activities that occur under your account.</li>
            <li>You must notify us immediately of any unauthorized use of your account.</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-10 mb-4 text-white">4. Ticket Purchases</h2>
          <h3 className="text-xl font-medium mt-6 mb-3 text-white">a. Pricing and Payment</h3>
          <ul className="list-disc pl-6 text-gray-300 mb-6 space-y-2">
            <li>All prices are displayed in Gambian Dalasi (GMD) unless otherwise stated.</li>
            <li>Payment is processed through our third-party payment providers (e.g., ModemPay).</li>
            <li>Tickets are issued electronically upon successful payment confirmation.</li>
          </ul>

          <h3 className="text-xl font-medium mt-6 mb-3 text-white">b. Refunds and Cancellations</h3>
          <ul className="list-disc pl-6 text-gray-300 mb-6 space-y-2">
            <li>All ticket sales are final unless the event is cancelled by the organizer.</li>
            <li>In case of event cancellation, refunds will be processed within 14 business days.</li>
            <li>Postponed events do not qualify for automatic refunds; tickets remain valid for the rescheduled date.</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-10 mb-4 text-white">5. Ticket Usage</h2>
          <ul className="list-disc pl-6 text-gray-300 mb-6 space-y-2">
            <li>Each ticket contains a unique QR code for entry validation.</li>
            <li>Tickets are non-transferable unless explicitly permitted by the event organizer.</li>
            <li>Reselling tickets at inflated prices is prohibited.</li>
            <li>We reserve the right to void tickets obtained through fraudulent means.</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-10 mb-4 text-white">6. Prohibited Conduct</h2>
          <p className="text-gray-300 mb-4">You agree not to:</p>
          <ul className="list-disc pl-6 text-gray-300 mb-6 space-y-2">
            <li>Use the Service for any unlawful purpose</li>
            <li>Attempt to gain unauthorized access to our systems</li>
            <li>Interfere with or disrupt the Service</li>
            <li>Create multiple accounts for fraudulent purposes</li>
            <li>Copy, modify, or distribute our content without permission</li>
            <li>Use automated systems to access the Service without our consent</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-10 mb-4 text-white">7. Intellectual Property</h2>
          <p className="text-gray-300 mb-6">
            All content, trademarks, and intellectual property on the Service are owned by Gam Ticket or our licensors. You may not use, copy, or distribute any content without our prior written consent.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4 text-white">8. Disclaimer of Warranties</h2>
          <p className="text-gray-300 mb-6">
            The Service is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not guarantee that the Service will be uninterrupted, secure, or error-free.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4 text-white">9. Limitation of Liability</h2>
          <p className="text-gray-300 mb-6">
            To the maximum extent permitted by law, Gam Ticket shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4 text-white">10. Indemnification</h2>
          <p className="text-gray-300 mb-6">
            You agree to indemnify and hold harmless Gam Ticket, its officers, directors, employees, and agents from any claims, damages, or expenses arising from your use of the Service or violation of these Terms.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4 text-white">11. Modifications to Terms</h2>
          <p className="text-gray-300 mb-6">
            We reserve the right to modify these Terms at any time. Changes will be posted on this page with an updated effective date. Your continued use of the Service after changes constitutes acceptance of the modified Terms.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4 text-white">12. Governing Law</h2>
          <p className="text-gray-300 mb-6">
            These Terms shall be governed by and construed in accordance with the laws of The Gambia, without regard to its conflict of law provisions.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4 text-white">13. Contact Us</h2>
          <p className="text-gray-300 mb-6">
            If you have questions about these Terms, please contact us at:
          </p>
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 mb-8">
            <p className="text-white font-semibold mb-2">Gam Ticket</p>
            <p className="text-gray-300">
              Email: <a href="mailto:support@gamticket.com" className="text-[#0055FF] hover:underline">support@gamticket.com</a>
            </p>
          </div>
        </article>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center text-gray-500 text-sm">
          <p>&copy; {new Date().getFullYear()} Gam Ticket. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
